import type { APIMessage } from '../contracts/chat-types.js';
import { MessageType } from '../contracts/chat-types.js';
import { compareSnowflakes } from '../contracts/snowflake.js';
import type { PendingMessage } from '../stores/state.js';

/** Uma linha da lista de mensagens (a lista é virtualizada por linha). */
export type Linha =
  | { tipo: 'inicio'; chave: 'inicio' }
  /** Só no fim (vendo mensagens antigas); no alto, o esqueleto fica no preenchimento da lista. */
  | { tipo: 'carregando'; chave: 'carregando-depois' }
  | { tipo: 'dia'; chave: string; data: string }
  | { tipo: 'novas'; chave: 'novas' }
  | {
      tipo: 'mensagem';
      chave: string;
      mensagem: APIMessage;
      /** Continuação do mesmo autor: sem avatar nem nome. */
      agrupada: boolean;
    }
  | {
      tipo: 'pendente';
      chave: string;
      pendente: PendingMessage;
      agrupada: boolean;
    };

/** Mensagens seguidas do mesmo autor juntam se vierem em até 7 minutos (Discord). */
const JANELA_AGRUPAR_MS = 7 * 60 * 1000;
const TIPOS_COMUNS = new Set<number>([
  MessageType.DEFAULT,
  MessageType.REPLY,
  MessageType.CHAT_INPUT_COMMAND,
  MessageType.CONTEXT_MENU_COMMAND,
]);

function autor(mensagem: APIMessage): string {
  return mensagem.webhook_id
    ? `${mensagem.webhook_id}:${mensagem.author.username}`
    : mensagem.author.id;
}

function dia(timestamp: string | number): string {
  const data = new Date(timestamp);
  return `${data.getFullYear()}-${data.getMonth()}-${data.getDate()}`;
}

/** A mensagem `atual` continua a `anterior` (mesmo autor, pouco tempo, nada no meio)? */
export function agrupa(anterior: APIMessage, atual: APIMessage): boolean {
  if (!TIPOS_COMUNS.has(anterior.type) || !TIPOS_COMUNS.has(atual.type)) {
    return false;
  }
  if (atual.type === MessageType.REPLY) return false;
  if (autor(anterior) !== autor(atual)) return false;
  const intervalo =
    new Date(atual.timestamp).getTime() -
    new Date(anterior.timestamp).getTime();
  return intervalo >= 0 && intervalo < JANELA_AGRUPAR_MS;
}

/**
 * As linhas da lista: começo do canal (quando chegou ao começo), divisória
 * de dia, divisória "Novas" antes da primeira mensagem não lida, as
 * mensagens (agrupadas quando é o caso) e, no fim, as que estão saindo.
 * O "carregando" do alto não é linha: uma linha ali viraria a âncora da
 * rolagem e a página antiga entraria sem segurar a posição.
 */
export function montarLinhas(entrada: {
  mensagens: APIMessage[];
  pendentes: PendingMessage[];
  temMaisAntes: boolean;
  temMaisDepois: boolean;
  /** Último lido quando o canal foi aberto (nulo: não marca "Novas"). */
  ultimoLido: string | null;
  euId: string | null;
}): Linha[] {
  const linhas: Linha[] = [];
  if (!entrada.temMaisAntes) linhas.push({ tipo: 'inicio', chave: 'inicio' });
  let anterior: APIMessage | null = null;
  let novasMarcada = false;
  for (const mensagem of entrada.mensagens) {
    let quebra = false;
    // A primeira carregada, com mais antigas por vir, fica sem divisória:
    // a linha do alto tem de ser uma mensagem (âncora estável da rolagem).
    const novoDia = anterior
      ? dia(anterior.timestamp) !== dia(mensagem.timestamp)
      : !entrada.temMaisAntes;
    if (novoDia) {
      linhas.push({
        tipo: 'dia',
        chave: `dia-${dia(mensagem.timestamp)}`,
        data: mensagem.timestamp,
      });
      quebra = true;
    }
    if (
      !novasMarcada &&
      entrada.ultimoLido &&
      compareSnowflakes(mensagem.id, entrada.ultimoLido) > 0 &&
      autor(mensagem) !== entrada.euId
    ) {
      linhas.push({ tipo: 'novas', chave: 'novas' });
      novasMarcada = true;
      quebra = true;
    }
    linhas.push({
      tipo: 'mensagem',
      chave: mensagem.id,
      mensagem,
      agrupada: !quebra && !!anterior && agrupa(anterior, mensagem),
    });
    anterior = mensagem;
  }
  if (entrada.temMaisDepois) {
    linhas.push({ tipo: 'carregando', chave: 'carregando-depois' });
    return linhas;
  }
  let ultimaDoAutor = anterior && autor(anterior) === entrada.euId;
  for (const pendente of entrada.pendentes) {
    linhas.push({
      tipo: 'pendente',
      chave: `pendente-${pendente.nonce}`,
      pendente,
      agrupada: !!ultimaDoAutor && !pendente.messageReference,
    });
    ultimaDoAutor = true;
  }
  return linhas;
}
